<template>
    <div class="edit-site container-fluid" v-if="!submitted">
        <BetterLink v-if="site.id > 0" class="float-right btn btn-sm btn-outline-primary text-dark" :to="{name: 'Site', params: {slug: site.slug}}">View</BetterLink>
        <h1 v-if="isNew">Submit a Hot Spring</h1>
        <h1 v-else>Edit {{ site.name }}</h1>
        <p class="mb-5" v-if="isNew && needsReview">If you know of a hot spring that is not listed you can submit it here. Please ensure that any details you submit are accurate and up to date.</p>
        
        <form @submit.prevent="submit" enctype="multipart/form-data" class="form">
            <div class="form-section shadow">
                <div class="row">
                    <div class="col-sm">
                        <div class="form-group required">
                            <label for="name">Name</label>
                            <input type="text" id="name" name="name" v-model.trim="$v.site.name.$model" class="form-control" :class="{ 'is-invalid': $v.site.name.$error }">
                            <div class="invalid-feedback" v-if="$v.site.name.$error">Name must be at least 4 characters long</div>
                        </div>
                    </div>
                    <div v-can="'sites.edit'" class="col-sm">
                        <div class="form-group">
                            <label for="slug">Slug</label>
                            <input type="text" id="slug" name="slug" v-model.trim="$v.site.slug.$model" class="form-control" placeholder="Leave blank to auto-generate" :class="{ 'is-invalid': $v.site.slug.$error }">
                            <div class="invalid-feedback" v-if="$v.site.slug.$error">Slug must be at least 4 characters long</div>
                        </div>
                    </div>
                </div>
            
                <div class="row">
                    <div v-can="'sites.approve'" class="col-sm">
                        <div class="form-group">
                            <label for="website">Status</label>
                            <div class="row">
                                <div class="col-sm-8">
                                    <CustomSelect id="listing_status" :options="statusOptions" v-model="site.listing_status" class="form-control" />
                                </div>
                                
                                <div class="col-sm">
                                    <Checkbox v-model="site.coming_soon" label="Coming Soon" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm">
                        <div class="form-group">
                            <label for="website">Official Website</label>
                            <input type="text" id="website" name="website" v-model.trim="$v.site.website.$model" class="form-control" :class="{ 'is-invalid': $v.site.website.$error }">
                            <div class="invalid-feedback" v-if="$v.site.website.$error">Please enter a valid URL <a v-b-tooltip.hover title="Include http:// or https://"><fa-icon :icon="['fal', 'info-circle']" /></a></div>
                        </div>
                    </div>
                    <div class="col-sm">
                        <div class="form-group">
                            <label for="alternate_names">Alternate Names</label>
                            <input type="text" id="alternate_names" name="alternate_names" v-model.trim="$v.site.alternate_names.$model" class="form-control" :class="{ 'is-invalid': $v.site.alternate_names.$error }">
                            <div class="invalid-feedback" v-if="$v.site.alternate_names.$error">Alternate names must be at least 4 characters long</div>
                        </div>
                    </div>
                </div>
                
                <div class="row">
                    <div class="col-sm form-group">
                        <label for="attire">Attire</label>
                        <CustomSelect id="attire" :options="attireOptions" v-model="site.attire" class="form-control" />
                    </div>
                    
                    <div class="col-sm form-group">
                        <label for="access">Access</label>
                        <CustomSelect id="access" :options="accessOptions" v-model="site.access" class="form-control" />
                    </div>
                    
                    <div class="col-sm form-group">
                        <label for="operation">Operation</label>
                        <CustomSelect id="operation" :options="operationOptions" v-model="site.operation" class="form-control" />
                    </div>
                    
                    <div class="col-sm form-group">
                        <Checkbox v-model="site.allows_kids" label="Allows Kids" class="mt-3" />
                        <Checkbox v-model="site.public_land" label="On Public Land" class="mt-1" />
                        <Checkbox v-model="site.private_property" label="Private Property" class="mt-1" />
                    </div>
                </div>
            </div>
            
            <div class="form-section shadow">
                <div class="form-group" v-if="headLoaded">
                    <label for="address">Location</label>
                    <GoogleAutocomplete
                        id="address"
                        placeholder="Search by location or address ..."
                        v-on:placechanged="getAddressData"
                    >
                    </GoogleAutocomplete>
                    
                    <ul v-if="site.location && typeof site.location == 'object'" class="list-unstyled list-inline mb-0">
                        <li v-for="(value, key) in site.location" :key="key" class="list-inline-item mr-4"><small><b>{{ key }}:</b> {{ value || 'null' }}</small></li>
                    </ul>
                </div>
            </div>
            
            <div class="form-section shadow">
                <div class="form-group">
                    <label for="short_description">Short Description</label>
                    <input type="text" id="short_description" name="short_description" v-model.trim="$v.site.short_description.$model" class="form-control" :class="{ 'is-invalid': $v.site.short_description.$error }">
                    <div class="invalid-feedback" v-if="$v.site.short_description.$error">Short description must be between 10 and 255 characters</div>
                </div>
                <div class="row">
                    <div class="col-sm">
                        <div class="form-group">
                            <label for="description">Description</label>
                            <textarea rows="6" id="description" name="description" v-model.trim="$v.site.description.$model" class="form-control" :class="{ 'is-invalid': $v.site.description.$error }"></textarea>
                            <div class="invalid-feedback" v-if="$v.site.description.$error">Description must be at least 10 characters</div>
                        </div>
                    </div>
                    <div class="col-sm">
                        <div class="form-group">
                            <label for="rules">Rules / Etiquette</label>
                            <textarea rows="6" id="rules" name="rules" v-model.trim="$v.site.rules.$model" class="form-control" :class="{ 'is-invalid': $v.site.rules.$error }"></textarea>
                            <div class="invalid-feedback" v-if="$v.site.rules.$error">Rules / etiquette must be at least 10 characters</div>
                        </div>
                    </div>
                    <div class="col-sm">
                        <div class="form-group">
                            <label for="how_to_access">How to Access</label>
                            <textarea rows="6" id="how_to_access" name="how_to_access" v-model.trim="$v.site.how_to_access.$model" class="form-control" :class="{ 'is-invalid': $v.site.how_to_access.$error }"></textarea>
                            <div class="invalid-feedback" v-if="$v.site.how_to_access.$error">How to access must be at least 10 characters</div>
                        </div>
                    </div>
                </div>
                <div class="row" v-can="'sites.approve'">
                    <div class="col-sm">
                        <div class="form-group">
                            <label for="alert_text">Alert Text</label>
                            <textarea rows="6" id="alert_text" name="alert_text" v-model.trim="$v.site.alert_text.$model" class="form-control" :class="{ 'is-invalid': $v.site.alert_text.$error }"></textarea>
                            <div class="invalid-feedback" v-if="$v.site.alert_text.$error">Alert text must be at least 10 characters</div>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="alert_style">Alert Style</label>
                            <CustomSelect id="alert_style" :options="alertStyleOptions" v-model="site.alert_style" class="form-control" />
                        </div>
                    </div>
                </div>
            </div>
            
            <div v-if="!isSuggesting" class="row">
                <div class="col-sm">
                    <div class="form-section shadow">
                        <h3>Photos</h3>
                        <ImageGallery ref="imageGallery"
                                       :existing-images="site.photos"
                                       :use-aws="true"
                                       @upload-complete="imageUploadComplete"></ImageGallery>
                    </div>
                </div>
                <div class="col-sm">
                    <div class="form-section shadow">
                        <h3>Amenities</h3>
                        <AmenityEditor v-if="site && site.amenities" v-model="site.amenities"></AmenityEditor>
                    </div>
                </div>
            </div>
            
            <div class="form-section shadow">
                <div class="row">
                    <div class="col-sm-3">
                        <h3>Pools</h3>
                        <div class="form-group mr-4">
                            <label for="number_of_pools">How Many?</label>
                            <input id="number_of_pools" type="number" v-model.trim="site.number_of_pools" class="form-control">
                        </div>
                    </div>
                    
                    <div class="col-sm">
                        <h3>Temperature (°F)</h3>
                        <div class="row">
                            <div class="col">
                                <div class="form-group mr-4">
                                    <label for="temp_low">Low</label>
                                    <input id="temp_low" type="number" v-model.trim="site.min_temp" class="form-control">
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mr-4">
                                    <label for="temp_high">High</label>
                                    <input id="temp_high" type="number" v-model.trim="site.max_temp" class="form-control">
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-sm">
                        <h3>Admission Price ($US)</h3>
                        <div class="row">
                            <div class="col">
                                <div class="form-group mr-4">
                                    <label for="price_low">Low</label>
                                    <CurrencyInput id="price_low" v-model.trim="site.min_price" class="form-control" />
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mr-4">
                                    <label for="price_high">High</label>
                                    <CurrencyInput id="price_high" v-model.trim="site.max_price" class="form-control" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="form-section shadow">
                <Checkbox v-model="hasHours" class="d-inline-block" /><h3 class="d-inline-block mb-0">Day Use Hours <small v-if="hasHours">(24 hour clock, sorry)</small></h3>
                <div class="d-flex mt-4" v-if="hasHours">
                    <div v-for="day in ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']" :key="day">
                        <div class="mr-4">
                            {{ day.toUpperCase() }}<br>
                            <div style="margin-top: -5px; margin-bottom:10px"><a href="#" @click.prevent="copyHours(day)"><small>Copy to all</small></a></div>
                        </div>
                        <div class="form-group mr-4">
                            <label class="mr-2" :for="'open_time_'+day">Open</label>
                            <input type="text" :id="'open_time_'+day" v-model.trim="site.hours[day].open" class="form-control">
                        </div>
                        <div class="form-group mr-4">
                            <label class="mr-2" :for="'close_time_'+day">Close</label>
                            <input type="text" :id="'close_time_'+day" v-model.trim="site.hours[day].close" class="form-control">
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="form-section shadow" v-can="'sites.edit'">
                <div class="row">
                    <div class="col-md-4 col-sm-5">
                        <h3>My Rating</h3>
                        <RatingChart :editable="true" :ratings="site.my_ratings" class="mb-3" @change="ratingChanged" />
                    </div>
                    <div class="col-sm">
                        <h3>My Attributes</h3>
                        <AttributeEditor v-model="site.my_environmental_attributes" />
                    </div>
                </div>
            </div>
            
            <SubmitButton :is-disabled="$v.$invalid" :is-saving="isSaving" />
        </form>
    </div>
    
    <div class="submitted container-fluid text-center my-5 py-5" v-else-if="submittedSuggestion">
        <h2 class="mb-4">Your Updates have Been Submitted</h2>
        <p class="lead">Thank you for suggesting updates to <b>drench.</b> We'll review the information soon and add it to the site if everything checks out.</p>
        <BetterLink class="btn btn-lg btn-outline-primary text-dark mt-4" :to="{name: 'Site', params: {slug: site.slug}}">Back to {{ site.name }}</BetterLink>
    </div>
    
    <div class="submitted container-fluid text-center my-5 py-5" v-else>
        <h2 class="mb-4">Your Submission has Been Saved</h2>
        <p class="lead">Thank you for submitting <i>{{ site.name }}</i> to <b>drench.</b> We'll review the information soon and add it to the site if everything checks out.</p>
        <BetterLink class="btn btn-lg btn-outline-primary text-dark mt-4" to="/">Return Home</BetterLink>
    </div>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import AmenityEditor from './amenities/AmenityEditor'
import RatingChart from "@/components/elements/RatingChart"
import AttributeEditor from "@/components/elements/AttributeEditor"
import ImageGallery from '@/components/form-controls/ImageGallery'
import CustomSelect from '@/components/form-controls/CustomSelect'
import Checkbox from '@/components/form-controls/Checkbox'
import SubmitButton from '@/components/form-controls/SubmitButton'
import GoogleAutocomplete from '@/components/form-controls/GoogleAutocomplete'
import { validationMixin } from 'vuelidate'
import required from 'vuelidate/lib/validators/required'
import minLength from 'vuelidate/lib/validators/minLength'
import maxLength from 'vuelidate/lib/validators/maxLength'
import url from 'vuelidate/lib/validators/url'


export default {
    name: 'EditSite',
    mixins: [
        validationMixin
    ],
    components: {
        RatingChart,
        AttributeEditor,
        ImageGallery,
        CustomSelect,
        Checkbox,
        SubmitButton,
        AmenityEditor,
        GoogleAutocomplete,
    },
    head: {
        title() {
            return {
                inner: this.site && this.site.name ? `Edit ${this.site.name}` : 'Add Site',
            }
        },
        script() {
            return [
                {
                    id: 'google_maps_api',
                    type: 'text/javascript',
                    src: this.initMapScript(),
                },
            ]
        },
    },
    validations: {
        site: {
            name: {
                required,
                minLength: minLength(4),
            },
            alternate_names: {
                minLength: minLength(4),
            },
            slug: {
                minLength: minLength(4),
            },
            website: {
                url,
            },
            short_description: {
                minLength: minLength(10),
                maxLength: maxLength(255),
            },
            description: {
                minLength: minLength(10),
            },
            alert_text: {
                minLength: minLength(10),
            },
            rules: {
                minLength: minLength(10),
            },
            how_to_access: {
                minLength: minLength(10),
            },
        },
    },
    data() {
        return {
            saveLoader: null,
            headLoaded: false,
            submitted: false,
            needsReview: !store.getters['account/can']('sites.approve', true),
            site: {
                name: '',
                alternate_names: '',
                slug: '',
                website: '',
                listing_status: this.needsReview ? 'Needs Review' : 'Approved',
                short_description: '',
                description: '',
                alert_text: '',
                rules: '',
                how_to_access: '',
                attire: 'Unspecified',
                access: 'Unspecified',
                operation: 'Unspecified',
                hours: null,
                location: {},
                my_ratings: {
                    safety: null,
                    access: null,
                    beauty: null,
                    comfort: null,
                },
                my_environmental_attributes: {
                    cleanliness: null,
                    comfort: null,
                    calmness: null,
                    development: null,
                    nudity_level: null,
                },
                amenities: [],
                private_property: false,
                public_land: false,
            },
            isSaving: false,
            
            booleanOptions: [
                {
                    label: 'Unspecified',
                    value: '',
                },
                {
                    label: 'Yes',
                    value: '',
                },
                {
                    label: 'No',
                    value: '',
                },
            ],
            statusOptions: [
                {
                    label: 'Needs Review',
                    value: 'Needs Review',
                },
                {
                    label: 'Approved',
                    value: 'Approved',
                },
                {
                    label: 'Denied',
                    value: 'Denied',
                },
            ],
            attireOptions: [
                {
                    label: 'Select attire ...',
                    value: 'Unspecified',
                },
                {
                    label: 'Clothing Optional',
                    value: 'Clothing Optional',
                },
                {
                    label: 'Nudity Required',
                    value: 'Nudity Required',
                },
                {
                    label: 'Clothing Required',
                    value: 'Clothing Required',
                },
                {
                    label: 'Clothing Optional After Dark',
                    value: 'Clothing Optional After Dark',
                },
            ],
            accessOptions: [
                {
                    label: 'Select access ...',
                    value: 'Unspecified',
                },
                {
                    label: 'Drive-up',
                    value: 'Drive-up',
                },
                {
                    label: 'Hike-in',
                    value: 'Hike-in',
                },
            ],
            operationOptions: [
                {
                    label: 'Select operation ...',
                    value: 'Unspecified',
                },
                {
                    label: 'Undeveloped',
                    value: 'Undeveloped',
                },
                {
                    label: 'Free',
                    value: 'Free',
                },
                {
                    label: 'Donation',
                    value: 'Donation',
                },
                {
                    label: 'Commercial',
                    value: 'Commercial',
                },
            ],
            alertStyleOptions: [
                {
                    label: 'Info',
                    value: 'info',
                },
                {
                    label: 'Success',
                    value: 'success',
                },
                {
                    label: 'Warning',
                    value: 'warning',
                },
                {
                    label: 'Danger',
                    value: 'danger',
                },
            ],
        }
    },
    computed: {
        isNew() {
            return this.$route.name == 'New Site'
        },
        hasHours: {
            get() {
                return this.site.hours != null
            },
            set(val) {
                if (val) {
                    this.site.hours = {"sunday": {"open": "9:00", "close": "22:00"}, "monday": {"open": "9:00", "close": "22:00"}, "tuesday": {"open": "9:00", "close": "22:00"}, "wednesday": {"open": "9:00", "close": "22:00"}, "thursday": {"open": "9:00", "close": "22:00"}, "friday": {"open": "9:00", "close": "22:00"}, "saturday": {"open": "9:00", "close": "22:00"}}
                } else {
                    this.site.hours = null
                }
            }
        },
        isSuggesting() {
            return !store.getters['account/can']('sites.edit')
        },
    },
    watch: {
        'site.public_land'(val) {
            if (val) {
                this.site.private_property = false
            }
        },
        'site.private_property'(val) {
            if (val) {
                this.site.public_land = false
            }
        },
    },
    methods: {
        initMapScript() {
            const mapScript = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${process.env.VUE_APP_GOOGLE_MAPS_JS_KEY}`
            let matches = document.querySelectorAll('script[src^="https://maps.googleapis.com/maps-api-v3/api/js/42/8/common.js"]');
            if (matches.length === 0) {
                return mapScript
            } else {
                this.headLoaded = true
                return ''
            }
        },
        loadSite() {
            if (!this.isNew) {
                let loader = this.$loading.show()
                axios({
                    url: process.env.VUE_APP_API_BASE_URL + '/sites/'+this.$route.params.id,
                    method: 'GET',
                }).then(response => {
                    this.site = response.data.data; 
                    this.$emit('updateHead')
                    loader.hide()
                }).catch(error => {
                    loader.hide()
                    console.log({ error: error })
                })
            }
        },
        getAddressData(addressData/*, placeResultData, id*/) {
            this.site.location = {}
            
            if (addressData.street_number && addressData.route) {
                this.site.location.address_1 = addressData.street_number + ' ' + addressData.route
            }
            if (addressData.subpremise) {
                this.site.location.address_2 = addressData.subpremise
            }
            if (addressData.locality) {
                this.site.location.locality = addressData.locality
            }
            if (addressData.administrative_area_level_1) {
                this.site.location.region = addressData.administrative_area_level_1
            }
            if (addressData.administrative_area_level_1_short) {
                this.site.location.region_short = addressData.administrative_area_level_1_short
            }
            if (addressData.postal_code) {
                this.site.location.postal_code = addressData.postal_code
            }
            if (addressData.country) {
                this.site.location.country = addressData.country
            }
            if (addressData.country_short) {
                this.site.location.country_short = addressData.country_short
            }
            if (addressData.latitude && addressData.longitude) {
                this.site.location.coords = {lat: addressData.latitude, lon: addressData.longitude}
            }
            // console.log(addressData)
        },
        submit() {
            this.saveLoader = this.$loading.show()
            if (!this.isSuggesting) {
                this.$refs.imageGallery.startUpload()
            } else {
                this.saveSite()
            }
        },
        imageUploadComplete(images) {
            this.site.photos = images
            this.saveSite()
        },
        saveSite() {
            let url = process.env.VUE_APP_API_BASE_URL + (this.isNew ? '/sites' : '/sites/'+this.$route.params.id)
            if (this.isSuggesting) {
                url += '/suggest'
            }
            axios({
                url: url,
                method: this.isNew ? 'POST' : 'PUT',
                data: this.site,
            }).then(response => {
                if (!this.isSuggesting) {
                    this.$refs.imageGallery.clearUploads()
                    this.site = response.data.data; 
                    this.$emit('updateHead')
                    this.$toast.success("Your changes have been saved.");
                    this.saveLoader.hide()
                    
                    if (this.isNew && this.needsReview) {
                        this.submitted = true
                    } else {
                        this.$router.replace({name: 'Edit Site', params: {id: this.site.slug}})
                    }
                } else {
                    this.loadSite()
                    this.submitted = true
                    this.submittedSuggestion = true
                }
                this.saveLoader.hide()
            }).catch(error => {
                this.saveLoader.hide()
                console.log({ error: error })
                this.$toast.error("There was a problem saving your changes.");
            })
        },
        ratingChanged(key, rating) {
            this.site.my_ratings[key] = rating
        },
        copyHours(day) {
            const valToCopy = this.site.hours[day]
            Object.keys(this.site.hours).forEach(day => {
                this.$set(this.site.hours, day, Object.assign({}, valToCopy))
            })
        },
    },
    created() {
        this.$on('okHead', function () {
            const sd = document.getElementById('google_maps_api')
            sd.onload = () => { this.headLoaded = true }
        });
        this.loadSite()
    },
    mounted() {
        // this.headLoaded = true
    },
    activated() {
        this.$emit('updateHead')
    },
};
</script>

<style lang="scss">
.form-section {
    background: white;
    padding: 30px;
    border-radius: 16px;
    margin-bottom: 40px;
}
</style>